import 'whatwg-fetch';
import * as cart from '@shopify/theme-cart';
import { EventEmitter } from 'events';
import { theme } from '../../constants/defaults';
import { trackKlaviyoAddedToCart } from '../../utils/klaviyoTracking';

export default class Cart extends EventEmitter {
  constructor(props) {
    super(props);

    this.config = {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json;',
        pragma: 'no-cache',
        'cache-control': 'no-cache',
      },
    };

    this.rootPrefix = theme.settings.root === '/' ? '/' : `${theme.settings.root}/`;
  }

  initReorder() {
    [...document.querySelectorAll('.js-cart-reorder')].forEach(el => {
      el.addEventListener('click', e => {
        e.preventDefault();

        const order = window.orders[`order${el.dataset.order}`];
        this.addItems(order);
      });
    });
  }

  getState() {
    return fetch(`${this.rootPrefix}cart.js`, this.config)
      .then(response => {
        if (!response.ok) {
          throw response;
        }
        return response.json();
      })
      .then(state => {
        this.updateCartCount(state.item_count, state.items);
        this.updateProductCount(state.items);
        return state;
      });
  }

  trackAddedToCart() {
    trackKlaviyoAddedToCart(this.rootPrefix, this.config);
  }

  addItems(items) {
    this.emit('adding');

    const config = {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json;',
      },
      method: 'POST',
      body: JSON.stringify({ items }),
    };

    fetch('/cart/add.js', config)
      .then(response => {
        if (!response.ok) {
          throw response;
        }
        return response.json();
      })
      .then(() => {
        this.trackAddedToCart();

        if (theme.settings.ajaxCartEnabled) {
          // Open ajax cart
          // will update the cart count as well
          const modalNamespace = 'cart-modal';
          const modal = window.modalManager.idModalMap.get(modalNamespace);
          modal.show();
        } else {
          // Update cart count
          this.getState();
        }

        // Send added event
        this.emit('added');
      })
      .catch(response => {
        response.json().then(error => {
          this.emit('error_add_item', error);
        });
      });
  }

  addItem(variantId, options, showModal = true) {
    this.emit('adding');
    cart
      .addItem(variantId, options)
      .then(() => {
        this.trackAddedToCart();

        if (theme.settings.ajaxCartEnabled && showModal) {
          // Open ajax cart
          // will update the cart count as well
          const modalNamespace = 'cart-modal';
          const modal = window.modalManager.idModalMap.get(modalNamespace);
          modal.show();
        } else {
          // Update cart count
          this.getState();
        }

        // Send added event
        this.emit('added');

        if (!showModal) {
          window.location.reload();
        }
      })
      .catch(response => {
        response.json().then(error => {
          this.emit('error_add_item', error);
        });
      });
  }

  async updateItem(key, options) {
    this.emit('updating');
    return cart
      .updateItem(key, options)
      .then(state => {
        this.updateCartCount(state.item_count, state.items);

        // Send updated event
        const updatedItem = state.items.find(item => item.key === key);
        this.emit('updated', updatedItem, state);
      })
      .catch(error => {
        this.emit('error_update_item', error);
      });
  }

  async removeItem(key) {
    this.emit('removing');
    return cart
      .removeItem(key)
      .then(state => {
        this.updateCartCount(state.item_count, state.items);

        // Send updated event
        this.emit('updated', null, state);
      })
      .catch(error => {
        this.emit('error_remove_item', error);
      });
  }

  async updateNote(note) {
    return cart.updateNote(note).catch(error => {
      this.emit('error_update_note', error);
    });
  }

  updateCartCount(itemCount, items) {
    const targetID = 47005477601620;
    const foundItem = items.find(item => item.id === targetID);

    // If the cart contains the pickup item, subtract 1 from the cart count
    if (foundItem) {
      [...document.querySelectorAll('.js-cart-count')].forEach(cartCountEl => {
        cartCountEl.innerHTML = itemCount - 1; // eslint-disable-line
      });
    } else {
      [...document.querySelectorAll('.js-cart-count')].forEach(cartCountEl => {
        cartCountEl.innerHTML = itemCount; // eslint-disable-line
      });
    }
  }

  updateProductCount(items) {
    [...document.querySelectorAll('.js-product-count')].forEach(product => {
      const { productId } = product.dataset;
      items.forEach(item => {
        if (item.product_id.toString() === productId) {
          product.innerHTML = item.quantity; // eslint-disable-line
          product.classList.add('active');
        }
      });
      if (items.length === 0) {
        product.innerHTML = '0'; // eslint-disable-line
        product.classList.remove('active');
      }
    });
  }

  static getInstance() {
    if (!Cart.instance) {
      Cart.instance = new Cart();
    }
    return Cart.instance;
  }
}
